/**
 * Generic Form Data Hook
 * Simple API-based data management without external dependencies
 * @version 6.0.0 - Removed React Query dependency
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { smartFetch } from '../services/apiClient';

interface UseFormDataOptions {
  siteCode: string;
  endpoint: string;
  dateRange?: {
    start: string;
    end: string;
  };
  enabled?: boolean;
}

export function useFormData<T>(options: UseFormDataOptions) {
  const { siteCode, endpoint, dateRange, enabled = true } = options;
  
  // State management
  const [data, setData] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<Error | null>(null);

  const getAuthHeaders = () => {
    // Check multiple possible token storage keys
    const token = sessionStorage.getItem('access_token') || 
                  localStorage.getItem('auth_token') || 
                  sessionStorage.getItem('access_token') ||
                  sessionStorage.getItem('token') ||
                  '';
    
    const headers: Record<string, string> = {
      'X-Site-Code': siteCode,
      'Content-Type': 'application/json',
    };
    
    // Only add Authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  };

  // Load data function - use useCallback with stable dependencies
  const loadData = useCallback(async () => {
    if (!enabled) return;
    
    setIsFetching(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      
      if (dateRange) {
        params.append('start_date', dateRange.start);
        params.append('end_date', dateRange.end);
      }

      const url = `http://localhost:8000/api/v1${endpoint}/range?${params}`;
      const result = await smartFetch<T[]>(url, {
        headers: getAuthHeaders(),
      });
      
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load data'));
    } finally {
      setIsFetching(false);
      setIsLoading(false);
    }
  }, [enabled, endpoint, siteCode, dateRange?.start, dateRange?.end]);

  // Save single item function
  const save = useCallback(async (payload: T): Promise<T> => {
    setIsSaving(true);
    setSaveError(null);
    
    try {
      const result = await smartFetch<T>(
        `${(typeof window !== 'undefined' ? (window as any).__API_BASE__ || `${window.location.protocol}//${window.location.hostname}:8000` : 'http://localhost:8000')}/api/v1${endpoint}/`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        }
      );
      
      // Optimistically update local data
      setData(prev => [...prev, result]);
      
      return result;
    } catch (err) {
      setSaveError(err instanceof Error ? err : new Error('Failed to save data'));
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [siteCode, endpoint]);

  // Batch save function
  const saveBatch = useCallback(async (payloads: T[]): Promise<any> => {
    setIsSaving(true);
    setSaveError(null);
    
    try {
      // Ensure trailing slash for Django compatibility
      const base = typeof window !== 'undefined' ? (window as any).__API_BASE__ || `${window.location.protocol}//${window.location.hostname}:8000` : 'http://localhost:8000';
      const bulkUrl = `${base}/api/v1${endpoint}/bulk/`;
const result = await smartFetch(
        bulkUrl,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(payloads),
        }
      );
// Don't reload data after batch save - user doesn't want it shown in frontend
      // await loadData();
      
      return result;
    } catch (err) {
      console.error(`[useFormData] Batch save error:`, err);
      setSaveError(err instanceof Error ? err : new Error('Failed to save batch data'));
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [siteCode, endpoint, loadData]);

  // Refetch function
  const refetch = useCallback(() => {
    setIsLoading(true);
    return loadData();
  }, [loadData]);

  // Use refs to track previous values and prevent unnecessary re-renders
  const prevDateStartRef = useRef<string | undefined>(dateRange?.start);
  const prevDateEndRef = useRef<string | undefined>(dateRange?.end);
  const prevEndpointRef = useRef<string>(endpoint);
  const prevSiteCodeRef = useRef<string>(siteCode);
  const isInitialMount = useRef<boolean>(true);

  // Load data on mount and when dependencies change
  useEffect(() => {
    if (!enabled) return;

    // Check if any dependency actually changed
    const dateStartChanged = prevDateStartRef.current !== dateRange?.start;
    const dateEndChanged = prevDateEndRef.current !== dateRange?.end;
    const endpointChanged = prevEndpointRef.current !== endpoint;
    const siteCodeChanged = prevSiteCodeRef.current !== siteCode;

    // Only load on initial mount or when dependencies actually change
    if (isInitialMount.current || dateStartChanged || dateEndChanged || endpointChanged || siteCodeChanged) {
      isInitialMount.current = false;
      
      // Update refs
      prevDateStartRef.current = dateRange?.start;
      prevDateEndRef.current = dateRange?.end;
      prevEndpointRef.current = endpoint;
      prevSiteCodeRef.current = siteCode;

      // Call loadData directly instead of including it in dependencies
      setIsLoading(true);
      setIsFetching(true);
      setError(null);
      
      (async () => {
        try {
          const params = new URLSearchParams();
          
          if (dateRange) {
            params.append('start_date', dateRange.start);
            params.append('end_date', dateRange.end);
          }

          const url = `http://localhost:8000/api/v1${endpoint}/range?${params}`;
          const result = await smartFetch<T[]>(url, {
            headers: getAuthHeaders(),
          });
          
          setData(result);
        } catch (err) {
          setError(err instanceof Error ? err : new Error('Failed to load data'));
        } finally {
          setIsFetching(false);
          setIsLoading(false);
        }
      })();
    }
  }, [enabled, dateRange?.start, dateRange?.end, endpoint, siteCode]);

  return {
    // Query state
    data,
    isLoading,
    isFetching,
    error,
    
    // Mutation functions
    save,
    saveBatch,
    
    // Mutation state
    isSaving,
    saveError,
    
    // Manual refetch
    refetch,
  };
}



